import _ from 'lodash';
import { produce } from 'immer';
import React from 'react';
import { useDispatch } from 'react-redux';

import agentsResource from 'src/actions/agent/agentsResource';
import answerLanguagesResource from 'src/actions/answers/answerLanguagesResource';
import answerVariablesResource from 'src/actions/answers/answerVariablesResource';
import businessHoursResource from 'src/actions/businessHours/businessHoursResource';
import chatCampaignsResource from 'src/actions/chatCampaigns/chatCampaignsResource';
import customerProfileDefResource from 'src/actions/customer/customerProfileDefResource';
import { Edit } from '../shared/rowActions/Edit';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import routingGroupsResource from 'src/actions/routingGroups/routingGroupsResource';
import RuleEditor from './RuleEditor';
import { RuleSetType } from 'src/actions/rules/ruleSetsResource';
import rulesResource, { Rule } from 'src/actions/rules/rulesResource';
import TablePage, { TablePageRender } from '../shared/TablePage';
import Toggle from 'src/presentation/components/forms/Toggle';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import useResource, { isLoadingStatus } from 'src/presentation/hooks/useResource';
import useTopics from 'src/presentation/hooks/useTopics';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import useAppPlatformDataAttributes from './hooks/useAppPlatformDataAttributes';

const dataAidPrefix = 'admin-rules';

const Rules: TablePageRender = ({ root }) => {
  const dispatch = useDispatch();
  const actions = (
    <RouterLinkButton data-aid={`${dataAidPrefix}-addButton`} to={`${root}/new`}>
      Create Rule
    </RouterLinkButton>
  );

  const columnConfig = [
    {
      attr: 'disabled',
      customRenderer: item => (
        <Toggle
          data-aid={`enable-rule-${item.id}`}
          value={!item.disabled}
          onChange={() => {
            toggleEnabled(item);
          }}
        />
      ),
      customStyle: () => ({ minWidth: '64px', width: '80px' }),
    },
    { attr: 'name', label: 'Name' },
  ];

  const isFeatureEnabled = useIsFeatureEnabled();
  const allowAppPlatformDataAttributes = isFeatureEnabled('appPlatformDataAttributes');

  // prefetch resources for editor page
  useDocumentResource(answerLanguagesResource);
  useAppPlatformDataAttributes({ fetch: allowAppPlatformDataAttributes });
  useDocumentResource(customerProfileDefResource);
  useResource(agentsResource);
  useResource(answerVariablesResource);
  useResource(businessHoursResource);
  useResource(chatCampaignsResource);
  useResource(routingGroupsResource);

  useTopics();

  const { errors, loadingState, values } = useResource(rulesResource, {
    fetch: true,
    params: { ruleSetType: RuleSetType.Automation },
  });

  const rules = _.chain(values)
    .filter(item => item.trigger.type != 'SMART_MATCH')
    .sortBy(item => item.order)
    .value();
  const description = (
    <div>
      Rules allow you to automate standard processes and procedures. Activities that meet your predefined criteria will
      execute and apply your Rules. <i>For instance, you can use a Rule to send an auto-reply to all new emails.</i>{' '}
      Please note that Rules cannot be removed once they are created.{' '}
      <a target="_blank" rel="noopener noreferrer" href="https://help.gladly.com/docs/what-are-rules/">
        Learn more about Rules.
      </a>
    </div>
  );

  function filterFunc(items, searchString) {
    if (!searchString) {
      return items;
    }
    return _.filter(
      items,
      item => item.trigger.type !== 'SMART_MATCH' && item.name.toLowerCase().indexOf(searchString.toLowerCase()) > -1
    );
  }

  const renderRowActions = ({ item }) => <Edit id={item.id} />;

  const toggleEnabled = (item: Rule) => {
    const toast = `Rule "${item.name}" ${item.disabled ? 'activated' : 'deactivated'}`;
    const updatedItem = produce(item, draft => {
      draft.disabled = !draft.disabled;
      draft._version++;
    });
    dispatch(
      rulesResource.effects.update({ id: item.id }, updatedItem, {
        customToastMsg: toast,
        disableWindowBack: true,
      })
    );
  };

  const updateRuleOrdering = ({ currentItemId, replaceItemId }) => {
    const newOrder = _.find(rules, { id: replaceItemId }).order;
    const ruleToUpdate = _.find(rules, { id: currentItemId });
    const updatedItem = produce(ruleToUpdate, draft => {
      draft.order = newOrder;
      draft._version++;
    });

    dispatch(
      rulesResource.effects.update({ id: currentItemId }, updatedItem, {
        customToastMsg: `Updated rule "${ruleToUpdate.name}"`,
        disableWindowBack: true,
      })
    );
  };

  return (
    <TablePage
      actions={actions}
      columnConfig={columnConfig}
      data-aid={`${dataAidPrefix}-page`}
      description={description}
      dragEndHandler={updateRuleOrdering}
      editor={RuleEditor}
      errors={errors}
      filterFunc={filterFunc}
      isLoading={isLoadingStatus(loadingState)}
      items={rules}
      name="Rules"
      renderRowActions={renderRowActions}
      resetErrors={rulesResource.actions.resetErrors}
      root={root}
    />
  );
};

export default Rules;
